import { useEffect, useRef, useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { Scale, Users, X } from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { Avatar } from "@/components/tuw/Avatar"
import SearchField from "@/components/tuw/SearchField"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { messageFreelancer, toggleFavorite } from "@/lib/hireActions"
import { initialsFor } from "@/components/tuw/hire/FreelancerAvatar"
import FreelancerRow from "@/components/tuw/hire/FreelancerRow"
import FilterBar from "@/components/tuw/hire/FilterBar"
import SortDropdown from "@/components/tuw/hire/SortDropdown"
import CompareModal from "@/components/tuw/hire/CompareModal"
import FunnelDeferredDialog from "@/components/tuw/hire/FunnelDeferredDialog"
import GuestShell from "@/components/tuw/viewingRoom/GuestShell"
import { GUEST_SORTS, guestCard, visitError } from "@/lib/viewingRoom/guest"
import AccountBox from "@/components/tuw/viewingRoom/AccountBox"
import { HandoffDialog, InviteFailed, canInvite, useInvite } from "@/components/tuw/viewingRoom/Invitation"
import { useResumed } from "@/components/tuw/viewingRoom/ResumedNotice"
import { NOTICES, VR, fill } from "@/lib/viewingRoom/wording"

// Build the query object from the validated `applied` state + a patch, dropping
// defaults so the URL stays clean (and shareable). Any filter change resets to
// page 1; pagination passes an explicit page.
function buildParams(applied, patch) {
  const a = { ...applied, ...patch }
  const resetPage = !("page" in patch)
  const params = {}
  if (a.q) params.q = a.q
  if (a.role) params.role = a.role
  if (a.tier) params.tier = a.tier
  if (a.niches?.length) params.niches = a.niches.join(",")
  if (a.languages?.length) params.languages = a.languages.join(",")
  if (a.online) params.online = "true"
  if (a.availability) params.availability = a.availability
  if (a.minRating) params.minRating = a.minRating
  if (a.minReliability) params.minReliability = a.minReliability
  if (a.noActive) params.noActive = "true"
  if (a.sort && a.sort !== "rating") params.sort = a.sort
  const page = resetPage ? 1 : a.page
  if (page && page > 1) params.page = page
  return params
}

function AppliedChips({ applied, facets, onChange, onClear }) {
  // The selected role tab already says which role is applied, so it has no
  // chip here; Clear all still resets it.
  const chips = []
  if (applied.tier) chips.push({ k: "tier", v: applied.tier, patch: { tier: null } })
  ;(applied.niches ?? []).forEach((n) => {
    const all = [...facets.niches.quick, ...facets.niches.more]
    chips.push({ k: `niche-${n}`, v: all.find((x) => x.key === n)?.label || n, patch: { niches: applied.niches.filter((x) => x !== n) } })
  })
  if (applied.availability) chips.push({ k: "avail", v: applied.availability === "now" ? "Available now" : "Within a week", patch: { availability: null } })
  if (applied.online) chips.push({ k: "online", v: "Online now", patch: { online: false } })
  if (applied.noActive) chips.push({ k: "noactive", v: "No active orders", patch: { noActive: false } })
  if (applied.minRating) chips.push({ k: "rating", v: `Rated ${applied.minRating}+`, patch: { minRating: 0 } })
  if (applied.minReliability) chips.push({ k: "reliab", v: `${applied.minReliability}%+ reliable`, patch: { minReliability: 0 } })
  ;(applied.languages ?? []).forEach((l) => chips.push({ k: `lang-${l}`, v: l, patch: { languages: applied.languages.filter((x) => x !== l) } }))

  if (chips.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-[8px]">
      {chips.map((c) => (
        <span key={c.k} className="inline-flex items-center gap-[6px] rounded-full border border-line-2 bg-card py-[5px] pr-[7px] pl-[11px] text-[12px] font-semibold text-muted-foreground">
          {c.v}
          <button type="button" onClick={() => onChange(c.patch)} aria-label={`Remove ${c.v}`}>
            <X className="size-[13px] text-ink-3 hover:text-primary" strokeWidth={2.2} />
          </button>
        </span>
      ))}
      <button type="button" onClick={onClear} className="px-[4px] py-[5px] text-[12px] font-semibold text-primary">
        Clear all
      </button>
    </div>
  )
}

const COMPARE_MAX = 3

function CompareTray({ selected, onClear, onCompare, guest, limitHit }) {
  if (selected.length < 2) return null
  const description = guest
    ? (limitHit ? fill(VR.compare.limit, { maximum: COMPARE_MAX }) : VR.compare.trayDescription)
    : "Side-by-side stats, niches & availability"

  return (
    <div className="fixed bottom-[22px] left-1/2 z-[70] flex w-[560px] max-w-[calc(100%-40px)] -translate-x-1/2 items-center gap-[14px] rounded-[14px] bg-foreground px-[16px] py-[13px] text-background shadow-[0_24px_60px_-18px_rgba(0,0,0,0.55)]">
      <div className="flex max-[561px]:hidden">
        {selected.map((f, i) => (
          <Avatar
            key={f.id}
            initials={initialsFor(f.penName)}
            name={f.penName}
            src={f.avatarUrl}
            size={34}
            className="ring-2 ring-foreground"
            style={i > 0 ? { marginLeft: -9 } : undefined}
          />
        ))}
      </div>
      <div className="text-[13px] font-semibold">
        {guest
          ? fill(VR.compare.trayTitle, { count: selected.length })
          : `Compare ${selected.length} freelancer${selected.length !== 1 ? "s" : ""}`}
        <div className="mt-px text-[11px] font-medium opacity-60">{description}</div>
      </div>
      <div className="flex-1" />
      <button type="button" onClick={onClear} className="text-[12px] font-semibold opacity-70 hover:opacity-100">{guest ? VR.compare.clear : "Clear"}</button>
      <button type="button" onClick={onCompare} className="flex items-center gap-[7px] rounded-[10px] bg-primary px-[18px] py-[10px] text-[13px] font-bold text-primary-foreground">
        <Scale className="size-[15px]" strokeWidth={2.2} />
        {guest ? VR.compare.button : "Compare"}
      </button>
    </div>
  )
}

// Viewing Room: a signed-out visitor gets the same page from the public
// presenter (`viewer: "guest"`), in the guest frame, with the approved guest
// words; Save opens the account box (AccountBox), Invite carries a writer or
// editor into Open House or hands any other freelancer off (Invitation). A
// customer sent here because the freelancer could no longer be acted on is
// told so.
function NoticeBanner({ text }) {
  if (!text) return null
  return (
    <div role="status" className="rounded-[12px] border border-border bg-secondary px-[16px] py-[12px] text-[13px] font-semibold text-foreground">
      {text}
    </div>
  )
}

export default function FindFreelancers() {
  const { freelancers: data, auth, viewer, noticeKey, account } = usePage().props
  const guest = viewer === "guest"
  const freelancers = guest
    ? { ...data, items: data.items.map(guestCard), facets: { ...data.facets, sorts: GUEST_SORTS } }
    : data
  const { items, applied, facets, total, page, perPage, pages } = freelancers
  const canMessage = auth?.canMessage ?? true
  const [loadError, setLoadError] = useState(null)
  const [limitHit, setLimitHit] = useState(false)
  const [saveFor, setSaveFor] = useState(null)
  const resumed = useResumed()
  const invite = useInvite()

  const [query, setQuery] = useState(applied.q || "")
  const [favOverride, setFavOverride] = useState({})
  const [compareMode, setCompareMode] = useState(false)
  const [selected, setSelected] = useState([])
  const [compareOpen, setCompareOpen] = useState(false)
  const [funnel, setFunnel] = useState({ open: false, freelancer: null, kind: "invite" })
  const searchTimer = useRef(null)

  // The order the customer arrived from: "Invite a freelancer" on an order
  // header links here with `?order=<id>` so the invite dialog can open on that
  // project. Read once from the URL this page mounted with and held for the
  // visit — buildParams deliberately rebuilds a clean query on every filter
  // change, so the hint leaves the address bar on the first filter while the
  // intent ("I'm hiring for this project") survives it, and a bookmarked or
  // shared URL can never carry a stale one back. Nothing is trusted here: the
  // dialog matches the id against the projects it loads for that freelancer.
  const [inviteOrderId] = useState(() => {
    const raw = new URLSearchParams(window.location.search).get("order")
    const id = Number(raw)
    return raw && Number.isInteger(id) && id > 0 ? id : null
  })

  // Keep the search box in sync if q is cleared elsewhere (chip / clear all).
  useEffect(() => { setQuery(applied.q || "") }, [applied.q])

  // The filters the customer has asked for, which can run ahead of `applied`
  // while a search is in flight. A newer search cancels the older one, so each
  // change must carry every earlier change with it, and the controls show
  // (and toggle from) these picks, not the last server answer: otherwise a
  // second quick tick is computed from stale state and undoes the first.
  // `intended` is read synchronously by back-to-back changes; `picks` renders it.
  const intended = useRef(applied)
  const [picks, setPicks] = useState(applied)
  useEffect(() => {
    intended.current = applied
    setPicks(applied)
  }, [applied])

  // A guest's failed search shows the approved words in place, not Inertia's
  // error page; the browsing limit says how long to wait.
  const guestErrors = guest
    ? {
        onStart: () => setLoadError(null),
        onHttpException: (response) => {
          setLoadError(visitError(response, VR.error.search))
          return false
        },
        onNetworkError: () => {
          setLoadError({ body: VR.error.search })
          return false
        },
      }
    : {}

  const go = (patch) => {
    const next = { ...intended.current, ...patch }
    intended.current = next
    setPicks(next)
    router.get("/find-freelancers", buildParams(next, patch), {
      preserveState: true,
      preserveScroll: true,
      only: ["freelancers"],
      replace: true,
      ...guestErrors,
    })
  }

  const onSearch = (value) => {
    setQuery(value)
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => go({ q: value }), 350)
  }

  const clearAll = () => {
    setQuery("")
    intended.current = {}
    setPicks({})
    router.get("/find-freelancers", {}, { preserveState: true, preserveScroll: true, only: ["freelancers"], replace: true, ...guestErrors })
  }

  const isFav = (f) => favOverride[f.id] ?? f.isFavorite
  const onToggleFav = (f) => {
    const next = !isFav(f)
    setFavOverride((o) => ({ ...o, [f.id]: next }))
    toggleFavorite(f.id, { only: ["navCounts"] })
  }

  const onMessage = (f) => f && messageFreelancer(f.id)

  const inCompare = (f) => selected.some((s) => s.id === f.id)
  const onToggleCompare = (f) => {
    const picked = selected.some((s) => s.id === f.id)
    setLimitHit(!picked && selected.length >= COMPARE_MAX)
    setSelected((cur) => {
      if (cur.some((s) => s.id === f.id)) return cur.filter((s) => s.id !== f.id)
      if (cur.length >= COMPARE_MAX) return cur
      return [...cur, f]
    })
    setCompareMode(true)
  }
  const toggleCompareMode = () => {
    setCompareMode((v) => {
      if (v) setSelected([])
      return !v
    })
  }

  const openFunnel = (freelancer, kind) => setFunnel({ open: true, freelancer, kind })

  const activeCount =
    (applied.role ? 1 : 0) + (applied.tier ? 1 : 0) + (applied.niches?.length || 0) +
    (applied.languages?.length || 0) + (applied.online ? 1 : 0) + (applied.availability ? 1 : 0) +
    (applied.minRating ? 1 : 0) + (applied.minReliability ? 1 : 0) + (applied.noActive ? 1 : 0)

  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1
  const rangeEnd = Math.min(page * perPage, total)

  const removeFromCompare = (f) => {
    setLimitHit(false)
    setSelected((cur) => cur.filter((s) => s.id !== f.id))
  }

  const sortOptions = guest ? GUEST_SORTS : facets.sorts

  return (
    <>
      {/* The roster becomes cards at ≤1200 (P8-H7 "Muster"); the filter bar
          replaces the old side rail at every width. Literals are the exclusive
          edge (`max-[861px]` = "at or below 860"), the convention PriceRail
          records. */}
      <div className="flex flex-col gap-[16px] px-[30px] pt-[22px] pb-[60px] max-[861px]:px-[14px] max-[861px]:pt-[16px] max-[861px]:pb-[48px]">
        <div>
          <h1 className="font-display text-[25px] font-bold tracking-[-0.5px]">{guest ? VR.search.title : "Find Freelancers"}</h1>
          <p className="mt-1 max-w-[560px] text-[13px] leading-[1.5] text-ink-3">
            {guest
              ? VR.search.intro
              : "Search a vetted roster of writers, editors, designers, illustrators, narrators, translators and Humanize AI specialists."}
          </p>
        </div>

        {guest && <NoticeBanner text={NOTICES[noticeKey]} />}
        {!guest && resumed === "unavailable" && <NoticeBanner text={VR.resumed.freelancerUnavailable} />}

        <FilterBar
          facets={facets}
          applied={picks}
          total={total}
          onChange={go}
          guest={guest}
          search={
            <SearchField
              className="min-w-0 flex-1 rounded-[11px] border-line-2 px-[14px] py-[11px] max-[561px]:basis-full"
              value={query}
              onChange={onSearch}
              placeholder={guest ? VR.search.placeholder : "Search the roster by name, niche or keyword…"}
            />
          }
        />

        <AppliedChips applied={picks} facets={facets} onChange={go} onClear={clearAll} />

        <div className="flex min-w-0 flex-col gap-[14px]">
          <div className="flex flex-wrap items-center gap-[10px]">
            <div className="text-[13px] font-semibold text-muted-foreground">
              <b className="font-display text-foreground">{total}</b> freelancer{total !== 1 ? "s" : ""} {activeCount ? "match your filters" : "in the roster"}
            </div>
            <div className="flex-1" />
            <SortDropdown value={applied.sort} options={sortOptions} onPick={(key) => go({ sort: key })} />
            <button
              type="button"
              onClick={toggleCompareMode}
              className={cn(
                "flex items-center gap-[7px] rounded-[11px] border px-[14px] py-[11px] text-[12.5px] font-semibold whitespace-nowrap transition-colors",
                compareMode ? "border-foreground bg-foreground text-background" : "border-border bg-card text-muted-foreground hover:border-line-2"
              )}
            >
              <Scale className="size-[15px]" strokeWidth={2} />
              {guest ? (compareMode ? VR.compare.active : VR.compare.button) : compareMode ? "Comparing" : "Compare"}
            </button>
          </div>

          {loadError && (
            <div role="alert" className="rounded-[12px] border border-border bg-card px-[16px] py-[14px] text-[13px] leading-[1.5]">
              {loadError.title && <div className="font-display font-bold text-foreground">{loadError.title}</div>}
              <div className="text-ink-3">{loadError.body}</div>
              <Button variant="outline" size="sm" className="mt-[10px]" onClick={() => go({})}>
                {VR.error.retry}
              </Button>
            </div>
          )}

          {guest && <InviteFailed invite={invite} />}
          {guest && <HandoffDialog invite={invite} />}

          {items.length === 0 ? (
            <div className="rounded-[15px] border border-border bg-card px-[20px] py-[54px] text-center">
              <div className="mx-auto mb-4 flex size-[58px] items-center justify-center rounded-[16px] border border-border bg-secondary">
                <Users className="size-[27px] text-ink-3" strokeWidth={1.7} />
              </div>
              <h3 className="font-display text-[18px] font-bold">{guest ? VR.search.emptyTitle : "No one in the roster fits yet"}</h3>
              <p className="mx-auto mt-[9px] max-w-[420px] text-[13.5px] leading-[1.55] text-ink-3">
                {guest
                  ? VR.search.emptyBody
                  : "That's a narrow brief. Try widening the tier or topic — or clear a filter or two to see more of the roster."}
              </p>
              <div className="mt-[18px] flex justify-center gap-[9px]">
                <Button variant="outline" onClick={clearAll}>
                  <X className="size-4" strokeWidth={2} />
                  {guest ? VR.search.clearFilters : "Clear filters"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-hidden rounded-[15px] border border-border bg-card max-[1201px]:flex max-[1201px]:flex-col max-[1201px]:gap-[12px] max-[1201px]:overflow-visible max-[1201px]:rounded-none max-[1201px]:border-0 max-[1201px]:bg-transparent">
              {items.map((f) => (
                <FreelancerRow
                  key={f.id}
                  f={f}
                  cardBelow={1200}
                  compareMode={compareMode}
                  compareChecked={inCompare(f)}
                  onToggleCompare={onToggleCompare}
                  isFavorite={guest ? false : isFav(f)}
                  onToggleFavorite={guest ? setSaveFor : onToggleFav}
                  onInvite={guest ? (canInvite(f) && !invite.busy ? invite.start : undefined) : (fl) => openFunnel(fl, "invite")}
                  guest={guest}
                />
              ))}
              <div className="flex items-center justify-between border-t border-border px-[22px] py-[15px] text-[12.5px] font-semibold text-ink-3 max-[1201px]:flex-wrap max-[1201px]:gap-[10px] max-[1201px]:border-t-0 max-[1201px]:px-[2px] max-[1201px]:py-0">
                <span>Showing {rangeStart}–{rangeEnd} of {total}</span>
                {pages > 1 && (
                  <div className="flex items-center gap-[8px]">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => go({ page: page - 1 })}>Previous</Button>
                    <span className="text-muted-foreground">Page {page} of {pages}</span>
                    <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => go({ page: page + 1 })}>Next</Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <CompareTray
        selected={selected}
        onClear={() => { setLimitHit(false); setSelected([]) }}
        onCompare={() => setCompareOpen(true)}
        guest={guest}
        limitHit={limitHit}
      />

      <CompareModal
        open={compareOpen}
        onOpenChange={setCompareOpen}
        freelancers={selected}
        onRemove={removeFromCompare}
        onInvite={(f) => {
          setCompareOpen(false)
          if (guest) invite.start(f)
          else openFunnel(f, "invite")
        }}
        guest={guest}
      />

      {guest && (
        <AccountBox account={account} onClose={() => setSaveFor(null)}
          request={saveFor && { action: "save", slug: saveFor.id, penName: saveFor.penName }} />
      )}
      {!guest && <FunnelDeferredDialog
        open={funnel.open}
        onOpenChange={(open) => setFunnel((s) => ({ ...s, open }))}
        freelancer={funnel.freelancer}
        kind={funnel.kind}
        preselectOrderId={inviteOrderId}
        onMessage={onMessage}
        canMessage={canMessage}
        onSave={(f) => f && !isFav(f) && onToggleFav(f)}
        isFavorite={funnel.freelancer ? isFav(funnel.freelancer) : false}
      />}
    </>
  )
}

// Inertia may call this with the raw props or the page element (see
// FreelancerProfile.layout).
FindFreelancers.layout = (page) =>
  (page.props ?? page).viewer === "guest" ? (
    <GuestShell>{page}</GuestShell>
  ) : (
    <AppShell active="Find Freelancers" crumb={["Hire", "Find Freelancers"]}>
      {page}
    </AppShell>
  )
